// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Drawing the layout canvas (M9-01, 11 §3.1).
//
// Separated from the React component for the usual reason and one better than usual: painting is
// the only part of a canvas surface that has no DOM in it at all — it is a sequence of calls against
// a 2-D context — so pulled out here it can be checked by handing it a context that records what it
// was told, which is the only way to assert that the machine was drawn at all rather than that a
// component rendered without throwing.
//
// **Everything is drawn in playfield metres and converted once.** Each shape asks `toScreen` for its
// points; nothing keeps a pixel coordinate. That is what makes a zoom a single number change rather
// than a second set of positions that has to be kept in step with the first.
//
// **The devices are drawn as what they are, not as dots.** A flipper is a tapered bat swept from its
// pivot at its rest angle, a slingshot is its rubber, a bumper is its skirt circle at its real
// radius. An author placing a device is judging a *gap* — will a ball fit between these two, does
// this rubber reach that lane — and a canvas of identical markers cannot answer that question at any
// zoom. The shapes here are the same measurements `sim/` collides against, read from the same
// fields.
//
// **The grid is drawn from the middle outward**, because the playfield's origin is its centre
// (PH-001) and a grid laid from a corner would put its lines a fraction of a millimetre off the
// centre line — the one line an author aligns to most.

import {type EditorPart as MachineDevice,deviceAnchors} from './parts'
import type {
  MachineLayer,
  Point2,
  Point3,
  ReachabilityMap,
} from '../../core/ts/machine'
import { } from '../../core/ts/machine'
import { isSplineLayer, profileOf } from './splines'
import type { SnapResult } from './snapping'
import { drawnGridMetres, toScreen, type PlayfieldSize, type Viewport } from './viewport'

/**
 * The slice of a 2-D context this file uses.
 *
 * Named rather than taken as `CanvasRenderingContext2D` so a test can pass a recorder without
 * implementing forty members it never calls, and so that adding a call is a deliberate widening of
 * this list rather than something that happens by accident.
 */
export interface PaintContext {
  // Widened to the real context's own types rather than narrowed to `string`. A gradient is never
  // assigned here, but a parameter that refuses one is a parameter a `CanvasRenderingContext2D`
  // cannot be passed to — and the alternative at the call site is a cast, which is the one thing
  // this interface exists to avoid.
  fillStyle: string | CanvasGradient | CanvasPattern
  strokeStyle: string | CanvasGradient | CanvasPattern
  lineWidth: number
  lineCap: CanvasLineCap
  font: string
  textAlign: CanvasTextAlign
  textBaseline: CanvasTextBaseline
  globalAlpha: number
  beginPath(): void
  moveTo(x: number, y: number): void
  lineTo(x: number, y: number): void
  arc(x: number, y: number, radius: number, start: number, end: number): void
  rect(x: number, y: number, width: number, height: number): void
  stroke(): void
  fill(): void
  clearRect(x: number, y: number, width: number, height: number): void
  fillText(text: string, x: number, y: number): void
  setLineDash(segments: readonly number[]): void
}

/** The colours, in one place so light and dark can hand in two sets. */
export interface CanvasTheme {
  readonly background: string
  readonly grid: string
  readonly gridStrong: string
  readonly rail: string
  readonly device: string
  readonly selected: string
  readonly handle: string
  readonly snapLine: string
  readonly label: string
  /** Where the ball can never go. Shaded, not outlined: it is an area, not an edge. */
  readonly unreachable: string
  /** Where it can go and cannot leave. */
  readonly trap: string
}

export const DARK_CANVAS: CanvasTheme = {
  background: '#0d1015',
  grid: '#1b212b',
  gridStrong: '#2b3441',
  rail: '#8ea2bd',
  device: '#7fd4ff',
  selected: '#ffd166',
  handle: '#ffffff',
  snapLine: '#ff7ab6',
  label: '#8ea2bd',
  unreachable: 'rgba(255, 74, 96, 0.16)',
  trap: 'rgba(255, 176, 32, 0.5)',
}

export const LIGHT_CANVAS: CanvasTheme = {
  background: '#f6f7f9',
  grid: '#e3e7ee',
  gridStrong: '#cbd3df',
  rail: '#43536b',
  device: '#0a6f97',
  selected: '#a86a00',
  handle: '#1b212b',
  snapLine: '#b4306f',
  label: '#43536b',
  unreachable: 'rgba(200, 24, 48, 0.13)',
  trap: 'rgba(190, 118, 0, 0.55)',
}

export interface PaintOptions {
  readonly view: Viewport
  readonly playfield: PlayfieldSize
  readonly devices: readonly MachineDevice[]
  /** Ramps and habitrails, drawn as the curve the ball actually follows (M9-04). */
  readonly layers?: readonly MachineLayer[] | undefined
  /** The ramp being shaped, whose control points get handles. */
  readonly selectedLayer?: string | undefined
  /** Which of those points is in hand, drawn larger so the two panels agree about it. */
  readonly selectedPoint?: number | undefined
  /** Under the glass, metres. A ramp is drawn brighter the higher it climbs. */
  readonly glassHeight?: number | undefined
  readonly selected?: string | undefined
  /** Where the device in hand would land, and why. Drawn as the guide lines that explain a snap. */
  readonly snap?: SnapResult | undefined
  /** The flood fill's own picture of the machine (M9-05). Drawn under everything. */
  readonly reachability?: ReachabilityMap | undefined
  /** Whether to draw it at all. The overlay is always on; this is the author's switch. */
  readonly showValidation?: boolean | undefined
  readonly theme: CanvasTheme
}

const TAU = Math.PI * 2

export function paint(ctx: PaintContext, options: PaintOptions): void {
  const { view, theme } = options
  ctx.clearRect(0, 0, view.width, view.height)
  ctx.fillStyle = theme.background
  ctx.beginPath()
  ctx.rect(0, 0, view.width, view.height)
  ctx.fill()

  paintGrid(ctx, options)
  // **Under the rails and everything else.** It is the floor of the picture — a wash saying where
  // the ball may be — and drawn on top it would grey out the machine an author is reading.
  paintReachability(ctx, options)
  paintRails(ctx, options)
  // **Under the devices.** A ramp passes over the playfield and the things on it, and drawing it
  // last would hide the bumper it flies above — which is exactly the relationship an author is
  // trying to see when they route one.
  paintSplines(ctx, options)
  for (const device of options.devices) {
    paintDevice(ctx, options, device, device.id === options.selected)
  }
  paintSnap(ctx, options)
}

/**
 * The grid, at whatever spacing is legible at this zoom.
 *
 * The design grid stays 1 mm — that is what a device snaps to — and what is *drawn* steps up as the
 * view zooms out. Every tenth line is stronger, which is what lets an author count in centimetres
 * without measuring.
 */
export function paintGrid(ctx: PaintContext, options: PaintOptions): void {
  const { view, playfield, theme } = options
  const step = drawnGridMetres(view)
  const halfWidth = playfield.width / 2
  const halfLength = playfield.length / 2
  ctx.lineWidth = 1
  ctx.setLineDash([])

  // Out from the centre in both directions, so the centre line is exactly on the centre.
  const across = Math.ceil(halfWidth / step)
  const along = Math.ceil(halfLength / step)
  for (let i = -across; i <= across; i++) {
    const x = i * step
    if (x < -halfWidth || x > halfWidth) continue
    ctx.strokeStyle = i % 10 === 0 ? theme.gridStrong : theme.grid
    line(ctx, view, [x, -halfLength], [x, halfLength])
  }
  for (let i = -along; i <= along; i++) {
    const y = i * step
    if (y < -halfLength || y > halfLength) continue
    ctx.strokeStyle = i % 10 === 0 ? theme.gridStrong : theme.grid
    line(ctx, view, [-halfWidth, y], [halfWidth, y])
  }
}

/** The side rails and the centre line: the playfield's own edges. */
export function paintRails(ctx: PaintContext, options: PaintOptions): void {
  const { view, playfield, theme } = options
  const halfWidth = playfield.width / 2
  const halfLength = playfield.length / 2
  ctx.strokeStyle = theme.rail
  ctx.lineWidth = 2
  ctx.setLineDash([])
  ctx.beginPath()
  const topLeft = toScreen(view, [-halfWidth, halfLength])
  const bottomRight = toScreen(view, [halfWidth, -halfLength])
  ctx.rect(topLeft[0], topLeft[1], bottomRight[0] - topLeft[0], bottomRight[1] - topLeft[1])
  ctx.stroke()

  // The centre line, dashed: it is a reference rather than a wall, and a solid one would read as a
  // rail down the middle of the machine.
  ctx.setLineDash([6, 6])
  ctx.lineWidth = 1
  line(ctx, view, [0, -halfLength], [0, halfLength])
  ctx.setLineDash([])
}

/**
 * One device, drawn as its own shape.
 *
 * Selected devices are drawn in the selection colour and given visible handles, because a handle
 * that is only there when you guess where it is is not a handle.
 */
export function paintDevice(
  ctx: PaintContext,
  options: PaintOptions,
  device: MachineDevice,
  selected: boolean,
): void {
  const { view, theme } = options
  ctx.strokeStyle = selected ? theme.selected : theme.device
  ctx.fillStyle = selected ? theme.selected : theme.device
  ctx.lineWidth = selected ? 3 : 2
  ctx.lineCap = 'round'
  ctx.setLineDash([])

  switch (device.kind) {
    case 'flasher': circle(ctx,view,device.position,device.radius??.026);break
    case 'flipper': {
      // The bat, swept from the pivot at its rest angle. `restAngle` is measured from the +x axis,
      // so a left flipper at −30° points down and to the right, which is what a real one does.
      const tip: Point2 = [
        device.pivot[0] + Math.cos(device.restAngle) * device.length * (device.handedness === 'left' ? 1 : -1),
        device.pivot[1] + Math.sin(device.restAngle) * device.length,
      ]
      ctx.lineWidth = Math.max(2, device.radiusPivot * 2 * view.pixelsPerMetre)
      line(ctx, view, device.pivot, tip)
      circle(ctx, view, device.pivot, device.radiusPivot)
      break
    }
    case 'popBumper':
      circle(ctx, view, device.centre, device.skirtRadius)
      break
    case 'magnet':
      ctx.setLineDash([4, 4])
      circle(ctx, view, device.centre, device.radius)
      ctx.setLineDash([])
      break
    case 'captiveBall':
      circle(ctx, view, device.centre, 0.0135)
      break
    case 'kicker':
    case 'plunger':
    case 'autoPlunger':
      circle(ctx, view, device.centre, device.captureRadius)
      break
    case 'rollover':
      if (device.region.shape === 'circle') {
        ctx.setLineDash([3, 3])
        circle(ctx, view, device.region.centre, device.region.radius)
        ctx.setLineDash([])
      } else {
        ctx.setLineDash([3, 3])
        const min = toScreen(view, device.region.min)
        const max = toScreen(view, device.region.max)
        ctx.beginPath()
        ctx.rect(min[0], max[1], max[0] - min[0], min[1] - max[1])
        ctx.stroke()
        ctx.setLineDash([])
      }
      break
    case 'slingshot':
    case 'standupTarget':
    case 'dropTarget':
    case 'spinner':
    case 'gate':
    case 'ballSaveGate':
    case 'diverter':
      ctx.lineWidth = selected ? 4 : 3
      line(ctx, view, device.a, device.b)
      break
    case 'dropTargetBank':
      // Nothing of its own to draw: it is its targets, and they draw themselves.
      break
  }

  if (!selected) return
  ctx.fillStyle = theme.handle
  for (const anchor of deviceAnchors(device)) {
    const at = toScreen(view, anchor.at)
    ctx.beginPath()
    ctx.arc(at[0], at[1], 4, 0, TAU)
    ctx.fill()
  }
}

/**
 * The guides that explain a snap.
 *
 * **A snap you cannot see is a snap that reads as the canvas ignoring the pointer.** A line is drawn
 * along each axis that was caught by something other than the grid, in the snap colour, so an author
 * can tell "it lined up with that slingshot" from "it went where I put it". The grid is not drawn —
 * every drag ends on the grid, so a line saying so would be permanent decoration.
 */
export function paintSnap(ctx: PaintContext, options: PaintOptions): void {
  const { view, playfield, theme, snap } = options
  if (snap === undefined) return
  ctx.strokeStyle = theme.snapLine
  ctx.lineWidth = 1
  ctx.setLineDash([2, 4])
  if (snap.x.rule !== 'grid') {
    line(ctx, view, [snap.at[0], -playfield.length / 2], [snap.at[0], playfield.length / 2])
  }
  if (snap.y.rule !== 'grid') {
    line(ctx, view, [-playfield.width / 2, snap.at[1]], [playfield.width / 2, snap.at[1]])
  }
  ctx.setLineDash([])

  // The point itself, so a drag has something under the pointer even before it is dropped.
  ctx.fillStyle = theme.snapLine
  const at = toScreen(view, snap.at)
  ctx.beginPath()
  ctx.arc(at[0], at[1], snap.onPoint ? 6 : 4, 0, TAU)
  ctx.fill()
}

/**
 * Where the ball can and cannot go (M9-05).
 *
 * **Cells, merged into runs along each row.** The fill is 2.5 mm, so a playfield is about two
 * hundred by four hundred cells and drawing each one as its own rectangle is eighty thousand fill
 * calls. A run of adjacent cells in one row is one rectangle, which takes it to a few hundred, and
 * the picture is identical.
 *
 * **Blocked is not drawn.** Where the ball's centre cannot go includes every wall, post and device
 * on the machine — shading it would trace the outline of everything the author has already drawn and
 * leave nothing visible. What is worth showing is the space that is *open* and cannot be got to: a
 * pocket behind a wall, a lane sealed by a misplaced post. That is the fault, and it is invisible
 * without this.
 */
export function paintReachability(ctx: PaintContext, options: PaintOptions): void {
  const map = options.reachability
  if (map === undefined || options.showValidation === false) return
  const { view, theme } = options

  for (const layer of [
    { mask: map.reachable, colour: theme.unreachable, invert: true },
    { mask: map.trapped, colour: theme.trap, invert: false },
  ]) {
    ctx.fillStyle = layer.colour
    for (let row = 0; row < map.rows; row++) {
      let runStart = -1
      for (let col = 0; col <= map.cols; col++) {
        const index = row * map.cols + col
        const open = col < map.cols && map.blocked[index] === 0
        const lit = open && (layer.invert ? map.reachable[index] === 0 : layer.mask[index] === 1)
        if (lit && runStart < 0) runStart = col
        if (lit || runStart < 0) continue
        // The run ended on the previous column.
        const x0 = map.minX + runStart * map.cell
        const x1 = map.minX + col * map.cell
        const y0 = map.minY + row * map.cell
        const a = toScreen(view, [x0, y0 + map.cell])
        const b = toScreen(view, [x1, y0])
        ctx.beginPath()
        ctx.rect(a[0], a[1], b[0] - a[0], b[1] - a[1])
        ctx.fill()
        runStart = -1
      }
    }
  }
}

/**
 * Ramps and habitrails, as the curve the ball follows.
 *
 * **Sampled by the simulation's own `Path`, through `profileOf`.** The control points are not the
 * shape — a Catmull-Rom spline passes through them and bulges between them — so a canvas that joined
 * them with straight lines would draw a ramp that is not the ramp being played, and the difference
 * is largest exactly where an author is looking: the bend.
 *
 * Height is drawn as brightness rather than as a second view, because the profile panel is the
 * second view and two of them would disagree. What this has to show is *where* on the playfield the
 * climb happens.
 */
export function paintSplines(ctx: PaintContext, options: PaintOptions): void {
  const { view, theme } = options
  const layers = options.layers ?? []
  const glass = options.glassHeight ?? 0.1016
  for (const layer of layers) {
    if (!isSplineLayer(layer)) continue
    const chosen = layer.id === options.selectedLayer
    const profile = profileOf(layer.spline, glass)
    if (profile.samples.length < 2) continue

    ctx.setLineDash([])
    ctx.lineCap = 'round'
    ctx.lineWidth = chosen ? 4 : 2
    // Each span drawn on its own, so the height can change along the curve. One path with one
    // colour would answer "how high is this ramp" with an average.
    for (let i = 1; i < profile.samples.length; i++) {
      const from = profile.samples[i - 1]
      const to = profile.samples[i]
      if (from === undefined || to === undefined) continue
      const height = profile.apex > 0 ? Math.max(0, to.z) / profile.apex : 0
      ctx.globalAlpha = 0.35 + 0.65 * height
      ctx.strokeStyle = chosen ? theme.selected : theme.rail
      ctx.beginPath()
      const a = toScreen(view, [from.x, from.y])
      const b = toScreen(view, [to.x, to.y])
      ctx.moveTo(a[0], a[1])
      ctx.lineTo(b[0], b[1])
      ctx.stroke()
    }
    ctx.globalAlpha = 1

    if (!chosen) continue
    const points = layer.spline as readonly Point3[]
    for (let index = 0; index < points.length; index++) {
      const point = points[index]
      if (point === undefined) continue
      const at = toScreen(view, [point[0], point[1]])
      const held = index === options.selectedPoint
      ctx.fillStyle = held ? theme.selected : theme.handle
      ctx.beginPath()
      ctx.arc(at[0], at[1], held ? 7 : 5, 0, TAU)
      ctx.fill()
    }
  }
}

function line(ctx: PaintContext, view: Viewport, from: Point2, to: Point2): void {
  const a = toScreen(view, from)
  const b = toScreen(view, to)
  ctx.beginPath()
  ctx.moveTo(a[0], a[1])
  ctx.lineTo(b[0], b[1])
  ctx.stroke()
}

function circle(ctx: PaintContext, view: Viewport, centre: Point2, radiusMetres: number): void {
  const at = toScreen(view, centre)
  ctx.beginPath()
  ctx.arc(at[0], at[1], Math.max(1, radiusMetres * view.pixelsPerMetre), 0, TAU)
  ctx.stroke()
}
